import React from 'react';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { Grid, LinearProgress, Typography, TextField, Button, Fade, Dialog } from '@material-ui/core';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogActions from '@material-ui/core/DialogActions';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { STATES } from '../Constants';
import { RouteService } from '../Services/RouteService';
import ItemSelection from '../components/ItemSelection';
import { Glyphicon } from 'react-bootstrap';
import { UserService } from '../Services/UserService';

const useStyles = makeStyles(theme => ({
    root: {
        margin: 0,
        padding: theme.spacing(2),
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    sectionHeading: {
        fontSize: theme.typography.pxToRem(16),
        fontWeight: theme.typography.fontWeightMedium,
    },
    heading: {
        fontSize: theme.typography.pxToRem(14),
        fontWeight: theme.typography.fontWeightMedium,
    },
    subheading: {
        fontSize: theme.typography.pxToRem(14),
        fontWeight: theme.typography.fontWeightRegular,
        textAlign: "left"
    },
    subheadingLink: {
        fontSize: theme.typography.pxToRem(14),
        fontWeight: theme.typography.fontWeightRegular,
        textAlign: "left",
        color: "blue",
        textDecoration: "underline",
        cursor: "pointer"
    },
    formControlAutoComplete: {
        zIndex: 3,
        margin: theme.spacing(1),
        [theme.breakpoints.up('md')]: {
            marginLeft: theme.spacing(0),
        },
    }
}));

const DialogTitle = (props => {
    const { children, classes, onClose, ...other } = props;
    return (
        <MuiDialogTitle disableTypography className={classes.root} {...other}>
            <Typography variant="h6">{children}</Typography>
            {onClose ? (
                <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            ) : null}
        </MuiDialogTitle>
    );
});

const DialogContent = withStyles(theme => ({
    root: {
        padding: theme.spacing(2),
    },
}))(MuiDialogContent);

const DialogActions = withStyles(theme => ({
    root: {
        margin: 0,
        padding: theme.spacing(1),
    },
}))(MuiDialogActions);

export default function AddCustomer(props) {
    const { id, maxWidth, fullWidth, user } = props;
    const [open, setOpen] = React.useState(id ? true : false);
    const [loading, setLoading] = React.useState(false);
    const [customer, setCustomer] = React.useState({ tags: [] });
    const [errors, setErrors] = React.useState({});
    const [masterRoutes, setMasterRoutes] = React.useState([]);
    const fieldSize = "small"

    React.useEffect(() => {
        setOpen(props.open);
        if (props.open) {
            fetchData();
        } else {
            setCustomer({ tags: [] });
        }
    }, [props.open])

    const fetchData = () => {
        setLoading(true)
        let list = [RouteService.getRouteList()]
        if (id) {
            list.push(UserService.getCustomerDetails(id))
        }

        Promise.all(list)
            .then((datas) => {
                console.log(datas);
                if (datas[0]) {
                    setMasterRoutes(datas[0].routes)
                }
                if(datas[1] && datas[0]) {
                    let c = datas[1]
                    c.building_name = datas[0].routes.find(r => r.id === c.building_name)
                    setCustomer(c)
                }

            })
            .catch(error => {
                console.log(error);

            })
            .finally(() => setLoading(false));
    }

    const classes = useStyles();

    const handleSave = () => {
        if (isValid()) {
            setLoading(true);
            let body = Object.assign({}, customer);
            body.building_name = customer.building_name.id

            UserService.createCustomer({'user-id' : user.id, 'user-name': user.name }, body)
                .then(() => {
                    setLoading(false);
                    handleClose(true);
                    props.showSnackBar(`Saved!!`, 'success');
                })
                .catch(() => {
                    setLoading(false);
                    props.showSnackBar('Something went wrong. Please try again.', 'error');
                });
        }
    }

    const isValid = () => {
        let isValid = true;
        let errorList = {...errors};
        if(!customer.name){
            isValid = false;
            errorList.name = true
        } else {
            errorList.name = false
        }
        
        if(!customer.mobile_no){
            isValid = false;
            errorList.mobile_no = true
        } else {
            errorList.mobile_no = false
        }
        
        if(!customer.building_name){
            isValid = false;
            errorList.building_name = true
        } else {
            errorList.building_name = false
        }
        
        if(!customer.flat){
            isValid = false;
            errorList.flat = true
        } else {
            errorList.flat = false
        }
        
        if(!customer.state){
            isValid = false;
            errorList.state = true
        } else {
            errorList.state = false
        }

        setErrors(e => ({...e, ...errorList}));
        return isValid;
    }

    const handleChange = e => {
        e.persist()
        setCustomer(old => ({ ...old, [e.target.name]: e.target.value }))
    }

    const handleChangeKV = (k, v) => {
        setCustomer(old => ({ ...old, [k]: v }))
    }

    const toggleTag = (tag) => {
        let tags = customer.tags
        if (tags.includes(tag)) {
            tags = tags.filter(t => t !== tag)
        } else {
            tags.push(tag)
        }
        setCustomer(old => ({ ...old, tags}))
    }


    const handleClose = (update = false) => {
        props.close(update);
        setCustomer({ tags: [] });
        setOpen(false);
    };

    return (
        <Dialog
            disableBackdropClick
            disableEscapeKeyDown
            onClose={() => handleClose()}
            aria-labelledby="customized-dialog-title"
            open={open}
            fullWidth={fullWidth}
            maxWidth={maxWidth || "md"}
            scroll='paper'>
            <DialogTitle id="customized-dialog-title" onClose={() => handleClose()} classes={classes}>
                {id ? `Customer Details` : `Create Customer`}
            </DialogTitle>
            <DialogContent dividers>
                <Grid container justify="flex-start" alignItems="center" direction="row">
                    <Grid item xs={12} lg={12}>
                        <Fade
                            in={loading}
                            style={{
                                transitionDelay: loading ? '10ms' : '0ms'
                            }}
                            unmountOnExit
                        >
                            <LinearProgress style={{ height: 5, width: "100%" }} color="secondary" />
                        </Fade>
                    </Grid>
                    <Grid item container xs={12} lg={12} spacing={1} alignItems="center">
                        <Grid item xs={12} lg={12}>
                            <TextField
                                id="name"
                                label="Customer Name"
                                placeholder="Customer Name"
                                name="name"
                                size={fieldSize}
                                value={customer.name || ''}
                                className={classes.textField}
                                margin="normal"
                                error={errors.name || false}
                                variant="outlined"
                                onChange={handleChange}
                                InputProps={{
                                    endAdornment: <span style={{minWidth: 50}}>VIP <Glyphicon component="span" glyph="star" onClick={(e) => toggleTag('VIP')} style={{ fontSize: 14, color: customer.tags.includes("VIP") ? "#FFDF00" : "#BDBDBD" }} /></span>
                                }}
                                required
                                fullWidth />
                        </Grid>
                        <Grid item xs={12} lg={12}>
                            <ItemSelection
                                label='Select Route'
                                size={fieldSize}
                                required
                                value={customer.building_name || null}
                                optionLabel='name'
                                error={errors.building_name || false}
                                formControlStyle={classes.formControlAutoComplete}
                                autoCompleteStyle={{ zIndex: 500, marginTop: 8, marginRight: 0 }}
                                options={masterRoutes}
                                selected={(route) => handleChangeKV('building_name', route)} />
                        </Grid>
                        <Grid item xs={6} lg={12}>
                            <TextField
                                id="mobile_no"
                                label="Mobile Number"
                                placeholder="Mobile Number"
                                name="mobile_no"
                                error={errors.mobile_no || false}
                                value={customer.mobile_no || ''}
                                className={classes.textField}
                                margin="normal"
                                variant="outlined"
                                size={fieldSize}
                                onChange={handleChange}
                                required
                                fullWidth />
                        </Grid>
                        <Grid item xs={6} lg={12}>
                            <TextField
                                id="email"
                                label="Email"
                                placeholder="Email"
                                name="email"
                                error={errors.email || false}
                                type="email"
                                value={customer.email || ''}
                                className={classes.textField}
                                margin="normal"
                                variant="outlined"
                                size={fieldSize}
                                onChange={handleChange}
                                fullWidth />
                        </Grid>
                        <Grid item xs={6} lg={12}>
                            <TextField
                                id="flat"
                                label="Address"
                                placeholder="Address"
                                error={errors.address || false}
                                name="flat"
                                value={customer.flat || ''}
                                className={classes.textField}
                                margin="normal"
                                variant="outlined"
                                size={fieldSize}
                                onChange={handleChange}
                                required
                                fullWidth />
                        </Grid>
                        <Grid item xs={6} lg={12}>
                            <ItemSelection
                                required
                                clearable={true}
                                label='State'
                                size={fieldSize}
                                error={errors.state || false}
                                value={(customer.state && STATES.find(s => s.code === customer.state)) || null}
                                optionLabel='name'
                                formControlStyle={classes.formControlAutoComplete}
                                autoCompleteStyle={{ zIndex: 500, marginTop: 8, marginRight: 0 }}
                                options={STATES}
                                selected={(state) => handleChangeKV('state', state.code)} />
                        </Grid>
                        <Grid item xs={6} lg={12}>
                            <TextField
                                id="location"
                                label="Latitude, Longitude"
                                placeholder="Latitude, Longitude"
                                name="location"
                                error={errors.location || false}
                                value={customer.location || ''}
                                className={customer.textField}
                                margin="normal"
                                variant="outlined"
                                size={fieldSize}
                                onChange={handleChange}
                                helperText="Enter location in the format Latitude, Longitude. eg: 13.12312,78.909090"
                                fullWidth />
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button autoFocus onClick={() => handleClose()} color="secondary">
                    Close
                </Button>
                <Button autoFocus onClick={handleSave} color="primary">
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}

AddCustomer.prototype = {
    open: PropTypes.bool.isRequired,
    close: PropTypes.func.isRequired,
    maxWidth: PropTypes.any,
    fullWidth: PropTypes.bool
};